import styled from "@emotion/styled"
import { keyframes } from "@emotion/core"
import { breakpoints, theme } from "../../../utils/styles"

export const Wrapper = styled.section`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background: ${theme.black};
  width: 100vw;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  padding: 72px 0;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.l}px) {
    padding: 56px 0;
  }
`

// Full-bleed atmospheric background again, heavily darkened by Scrim so it
// reads as "mostly black" like the reference layout, with just a hint of
// motion visible rather than competing with the denser card content.
export const Media = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  z-index: 0;
`

export const Scrim = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.8);
`

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`

export const ContentZone = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 6vw;
  display: flex;
  flex-direction: column;
  animation: ${fadeUp} 0.9s cubic-bezier(0.16, 1, 0.3, 1) both;
`

export const HeadlineRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 32px;

  @media (max-width: ${breakpoints.l}px) {
    margin-bottom: 24px;
  }
`

export const TitleCol = styled.div`
  margin-bottom: 20px;
`

export const ParagraphCol = styled.div`
  max-width: 100%;
`

export const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${theme.primary};
  margin-bottom: 16px;

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${theme.primary};
  }
`

export const Paragraph = styled.p`
  margin: 0;
  font-size: clamp(15px, 1.2vw, 17px);
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.88);
`

export const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 32px;
`

export const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: rgba(255, 255, 255, 0.18);
`

export const LoopLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${theme.primary};
  margin-bottom: 8px;

  svg {
    width: 16px;
    height: 16px;
    stroke: ${theme.primary};
  }
`

export const StepsRowWrap = styled.div`
  position: relative;
  padding-top: 36px;
  margin-bottom: 48px;
`

// Shared "chase" glow: each segment (bracket, then each arrow in turn)
// flashes bright early in its own cycle, then stays dim until it loops.
// Staggering the same animation's delay across segments makes the glow
// read as travelling around the loop rather than everything pulsing
// in unison.
const LOOP_PERIOD = 4.8
const glowPulse = keyframes`
  0% { opacity: 0.4; filter: drop-shadow(0 0 0px ${theme.primary}); }
  6% { opacity: 1; filter: drop-shadow(0 0 10px ${theme.primary}); }
  25% { opacity: 0.4; filter: drop-shadow(0 0 0px ${theme.primary}); }
  100% { opacity: 0.4; filter: drop-shadow(0 0 0px ${theme.primary}); }
`

// Arrowhead stays fully opaque at all times (unlike the bracket/arrows,
// which dim between pulses) — it's a fixed marker, not part of the
// travelling glow, so it shouldn't ever look washed out or let the
// background show through it.
const arrowGlowPulse = keyframes`
  0% { filter: drop-shadow(0 0 0px ${theme.primary}); }
  6% { filter: drop-shadow(0 0 10px ${theme.primary}); }
  25% { filter: drop-shadow(0 0 0px ${theme.primary}); }
  100% { filter: drop-shadow(0 0 0px ${theme.primary}); }
`

export const LoopBracket = styled.div`
  position: absolute;
  top: 0;
  left: 11%;
  right: 11%;
  height: 36px;
  border: 2px dashed ${theme.primary};
  border-bottom: none;
  border-radius: 20px 20px 0 0;
  pointer-events: none;
  animation: ${glowPulse} ${LOOP_PERIOD}s ease-in-out infinite;
  animation-delay: 0s;

  @media (max-width: ${breakpoints.l}px) {
    display: none;
  }
`

export const LoopArrowDown = styled.svg`
  position: absolute;
  top: 27px;
  left: 11%;
  transform: translateX(-50%);
  width: 13px;
  height: 9px;
  overflow: visible;
  color: ${theme.primary};
  opacity: 1;
  animation: ${arrowGlowPulse} ${LOOP_PERIOD}s ease-in-out infinite;
  animation-delay: 0s;

  @media (max-width: ${breakpoints.l}px) {
    display: none;
  }
`

export const StepsRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;

  @media (max-width: ${breakpoints.l}px) {
    flex-direction: column;
    gap: 16px;
  }
`

export const StepCard = styled.div`
  flex: 1;
  background: ${theme.surface}CC;
  border-top: 3px solid ${theme.primary};
  border-radius: 10px;
  padding: 20px;
  transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.35s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: ${theme.primaryLight};
  }
`

export const StepIconWrap = styled.div`
  width: 22px;
  height: 22px;
  margin-bottom: 12px;

  svg {
    width: 22px;
    height: 22px;
    stroke: ${theme.primary};
  }
`

export const StepLabel = styled.h3`
  margin: 0 0 6px 0;
  font-size: 15.5px;
  font-weight: 700;
  color: ${theme.primary};
  font-family: Nunito, sans-serif;
`

export const StepText = styled.p`
  margin: 0;
  font-size: 13.5px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.78);
`

export const StepArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  flex-shrink: 0;
  color: ${theme.primary};
  font-size: 22px;
  font-weight: 700;
  animation: ${glowPulse} ${LOOP_PERIOD}s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay || 0}s;

  @media (max-width: ${breakpoints.l}px) {
    display: none;
  }
`

export const ResultsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;

  @media (max-width: ${breakpoints.l}px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`

export const ResultItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`

export const ResultIconWrap = styled.div`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-top: 3px;

  svg {
    width: 20px;
    height: 20px;
    stroke: ${theme.primary};
  }
`

export const ResultLabel = styled.h3`
  margin: 0 0 4px 0;
  font-size: 15px;
  font-weight: 700;
  color: ${theme.primary};
  font-family: Nunito, sans-serif;
`

export const ResultText = styled.p`
  margin: 0;
  font-size: 13.5px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.78);
`

export const NextButtonRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 48px;
`

export const NextButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  background: ${theme.primary};
  color: ${theme.black};
  font-weight: 700;
  font-size: 15px;
  font-family: Nunito, sans-serif;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.25s ease;
  }

  &:hover {
    transform: translateY(3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.35);
  }

  &:hover svg {
    transform: translateY(2px);
  }
`
